/**
 * Claim-билет: след в браузере между «нажал в портале» и «вошёл в Clerk».
 *
 * Билет приезжает в query (`/link-portal?claim=…`), но Clerk уводит человека
 * через свою форму входа и возвращает уже по своему redirect-URL. След в
 * `sessionStorage` переживает этот круг и — важнее — переживает ПРОВАЛ обмена:
 * гейт в `__root.tsx` по нему понимает, что человек с пустым списком
 * организаций пришёл из CRM-портала, и уводит его на объяснение вместо
 * молчаливого создания ВТОРОЙ организации. Ровно этим кончалась протухшая
 * ссылка до появления следа.
 */

const STORAGE_KEY = 'aelo.portalClaim';

/**
 * Билет из адреса страницы.
 *
 * Основное место — ФРАГМЕНТ (`#claim=…`): он не уходит на сервер, не попадает
 * в `Referer` и в логи Cloudflare/ui-worker. Тем же приёмом и по той же причине
 * `lib/embedded-session.ts` переносит bootstrap-токен из query во фрагмент.
 *
 * Query читается тоже — ради ссылок, выданных до этого перехода: билет живёт
 * час, и выкидывать людей с уже открытыми вкладками незачем.
 */
export function claimTokenFromLocation(search: string, hash = ''): string | null {
  const fromHash = new URLSearchParams(hash.replace(/^#/, '')).get('claim');
  if (fromHash) return fromHash;

  const fromQuery = new URLSearchParams(search).get('claim');
  return fromQuery && fromQuery.length > 0 ? fromQuery : null;
}

/**
 * Сохранить билет на время круга через форму Clerk.
 *
 * Возвращает `false`, если хранилище недоступно (private mode, запрет
 * сторонних данных). Вызывающая сторона по этому признаку решает, тащить ли
 * билет в адрес возврата: в норме адрес остаётся чистым.
 */
export function rememberClaimToken(token: string): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Убрать билет из адреса страницы, сохранив путь.
 *
 * Билет — пропуск в организацию: в истории браузера и её синхроне между
 * устройствами он живёт заметно дольше, чем нужен. Чистятся ОБА места —
 * фрагмент (новые ссылки) и query (выданные до перехода на фрагмент).
 * `replaceState`, а не `pushState`: лишняя запись в истории вернула бы билет
 * по кнопке «назад».
 */
export function scrubClaimFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const hadHash = hashParams.has('claim');
    const hadQuery = url.searchParams.has('claim');
    if (!hadHash && !hadQuery) return;

    url.searchParams.delete('claim');
    hashParams.delete('claim');
    const rest = hashParams.toString();

    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${rest ? `#${rest}` : ''}`
    );
  } catch {
    // История недоступна (или мы не в браузере) — вход это ломать не должно.
  }
}

export function storedClaimToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Стереть след. Вызывается после удавшейся привязки И после того, как человек
 * сознательно выбрал «создать отдельную организацию»: иначе гейт уводил бы
 * его с онбординга обратно на объяснение при каждой загрузке.
 */
export function forgetClaimToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // см. rememberClaimToken
  }
}
